import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/** Axios instance — credentials: 'include' to send httpOnly session cookies. */
export const api = axios.create({
  baseURL: API,
  timeout: 15000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ---------- Public ----------
export async function submitLead(payload) {
  const { data } = await api.post("/leads", payload);
  return data;
}

export async function fetchImpact() {
  const { data } = await api.get("/impact");
  return data;
}

// ---------- Auth ----------
export async function exchangeSession(session_id) {
  const { data } = await api.post("/auth/session", { session_id });
  return data;
}

export async function fetchMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

export async function logout() {
  const { data } = await api.post("/auth/logout");
  return data;
}

// ---------- Admin ----------
export async function fetchLeads({ lead_type, limit = 200 } = {}) {
  const params = { limit };
  if (lead_type) params.lead_type = lead_type;
  const { data } = await api.get("/leads", { params });
  return data;
}

export async function markLeadContacted(id) {
  const { data } = await api.post(`/leads/${id}/mark-contacted`);
  return data;
}

export async function updateImpact(patch) {
  const { data } = await api.put("/impact", patch);
  return data;
}

// ---------- Public Catalogue ----------
export async function fetchCollections() {
  const { data } = await api.get("/collections");
  return data;
}

export async function fetchCollection(slug) {
  const { data } = await api.get(`/collections/${slug}`);
  return data;
}

export async function fetchProducts({ collection } = {}) {
  const params = {};
  if (collection) params.collection = collection;
  const { data } = await api.get("/products", { params });
  return data;
}

export async function fetchProduct(slug) {
  const { data } = await api.get(`/products/${slug}`);
  return data;
}

// ---------- Admin Catalogue ----------
export async function adminListCollections() {
  const { data } = await api.get("/collections", { params: {} });
  return data;
}

export async function adminCreateCollection(payload) {
  const { data } = await api.post("/admin/collections", payload);
  return data;
}

export async function adminUpdateCollection(id, payload) {
  const { data } = await api.put(`/admin/collections/${id}`, payload);
  return data;
}

export async function adminDeleteCollection(id, force = false) {
  const { data } = await api.delete(`/admin/collections/${id}`, { params: { force } });
  return data;
}

export async function adminListProducts() {
  const { data } = await api.get("/products");
  return data;
}

export async function adminCreateProduct(payload) {
  const { data } = await api.post("/admin/products", payload);
  return data;
}

export async function adminUpdateProduct(id, payload) {
  const { data } = await api.put(`/admin/products/${id}`, payload);
  return data;
}

export async function adminDeleteProduct(id) {
  const { data } = await api.delete(`/admin/products/${id}`);
  return data;
}

export async function adminUploadImage(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/uploads", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return data; // { url, filename, size }
}

// ---------- Articles ----------
export async function fetchArticles({ featured, limit } = {}) {
  const params = {};
  if (featured !== undefined) params.featured = featured;
  if (limit) params.limit = limit;
  const { data } = await api.get("/articles", { params });
  return data;
}

export async function fetchArticle(slug) {
  const { data } = await api.get(`/articles/${slug}`);
  return data;
}

export async function adminListArticles() {
  const { data } = await api.get("/articles", { params: { limit: 500 } });
  return data;
}

export async function adminCreateArticle(payload) {
  const { data } = await api.post("/admin/articles", payload);
  return data;
}

export async function adminUpdateArticle(id, payload) {
  const { data } = await api.put(`/admin/articles/${id}`, payload);
  return data;
}

export async function adminDeleteArticle(id) {
  const { data } = await api.delete(`/admin/articles/${id}`);
  return data;
}

// ---------- Landing Pages ----------
export async function fetchLandingPages({ type } = {}) {
  const params = {};
  if (type) params.type = type;
  const { data } = await api.get("/landing-pages", { params });
  return data;
}

export async function fetchLandingPage(slug) {
  const { data } = await api.get(`/landing-pages/${slug}`);
  return data;
}

export async function adminListLandingPages() {
  const { data } = await api.get("/landing-pages");
  return data;
}

export async function adminCreateLandingPage(payload) {
  const { data } = await api.post("/admin/landing-pages", payload);
  return data;
}

export async function adminUpdateLandingPage(id, payload) {
  const { data } = await api.put(`/admin/landing-pages/${id}`, payload);
  return data;
}

export async function adminDeleteLandingPage(id) {
  const { data } = await api.delete(`/admin/landing-pages/${id}`);
  return data;
}

// ---------- Site Content (CMS-managed static assets) ----------
export async function fetchSiteContent() {
  const { data } = await api.get("/site-content");
  return data;
}

export async function adminSetSiteContent(key, value) {
  const { data } = await api.put(`/admin/site-content/${key}`, { value });
  return data;
}

// ---------- Green Gift Projects ----------
export async function fetchGreenGiftProjects() {
  const { data } = await api.get("/green-gift-projects");
  return data;
}

export async function adminListGreenGiftProjects() {
  const { data } = await api.get("/green-gift-projects");
  return data;
}

export async function adminCreateGreenGiftProject(payload) {
  const { data } = await api.post("/admin/green-gift-projects", payload);
  return data;
}

export async function adminUpdateGreenGiftProject(id, payload) {
  const { data } = await api.put(`/admin/green-gift-projects/${id}`, payload);
  return data;
}

export async function adminDeleteGreenGiftProject(id) {
  const { data } = await api.delete(`/admin/green-gift-projects/${id}`);
  return data;
}

// ---------- Partner Benefit Brands ----------
export async function fetchPartnerBrands() {
  const { data } = await api.get("/partner-brands");
  return data;
}

export async function adminListPartnerBrands() {
  const { data } = await api.get("/partner-brands");
  return data;
}

export async function adminCreatePartnerBrand(payload) {
  const { data } = await api.post("/admin/partner-brands", payload);
  return data;
}

export async function adminUpdatePartnerBrand(id, payload) {
  const { data } = await api.put(`/admin/partner-brands/${id}`, payload);
  return data;
}

export async function adminDeletePartnerBrand(id) {
  const { data } = await api.delete(`/admin/partner-brands/${id}`);
  return data;
}
